import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Content } from '@layout';
import useIsMounted from '@hooks/useIsMounted';
import styles from './Layout.module.scss';
import { generateBackgroundImage, getScrollbarWidth } from '@helpers';
import { isString } from 'lodash';
import BackgroundImage from 'gatsby-background-image';
import classNames from 'classnames';

const Section = React.forwardRef(
  (
    {
      tag: Tag = 'section',
      className,
      wrapContent = true,
      fullWidth,
      style = {},
      isDark,
      textAlign,
      contentWrapperClassName,
      contentClassName,
      contentWrapperStyle = {},
      contentStyle = {},
      maxContentWidth,
      fillContainer,
      children,
      backgroundImage,
      backgroundColor,
      showDots,
      rightAlignDots,
      hideDots,
      hideUntilBackgroundLoad: _hideUntilBackgroundLoad,
      backgroundLoadTimeout = 1500,
      hasBackground,
      onLoad,
      contentBefore,
      contentAfter,
      ...restProps
    },
    ref
  ) => {
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const maxWidth =
      fullWidth && scrollbarWidth
        ? `calc(100vw - ${scrollbarWidth}px)`
        : null;

    useEffect(() => {
      setScrollbarWidth(getScrollbarWidth());
    }, []);

    const props = {
      ref,
      style: { maxWidth, ...style, backgroundColor },
      className: classNames(styles.section, className, {
        [styles.fillContainer]: fillContainer,
        [styles.fullWidth]: fullWidth,
        [styles.colorSection]: hasBackground,
        [styles.dark]: isDark,
        [styles.dotSection]: showDots,
        [styles.hideDots]: hideDots,
        [styles.rightDots]: rightAlignDots,
        [styles.textCenter]: textAlign === 'center',
        [styles.textRight]: textAlign === 'right'
      }),
      ...restProps
    };

    const hasInitialized = useRef(false);
    const isMounted = useIsMounted();

    // Optionally hide section until background
    // image is loaded to prevent flicker
    const hideUntilBackgroundLoad =
      _hideUntilBackgroundLoad && backgroundImage;
    const [isHidden, setIsHidden] = useState(hideUntilBackgroundLoad);
    const loadTimer = useRef(null);
    const loadHandler = useCallback(() => {
      if (isMounted.current) {
        clearTimeout(loadTimer.current);
        setIsHidden(false);

        if (onLoad) {
          onLoad();
        }
      }
    }, [isMounted, onLoad]);

    useEffect(() => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;

        if (hideUntilBackgroundLoad) {
          if (backgroundLoadTimeout) {
            loadTimer.current = setTimeout(
              loadHandler,
              backgroundLoadTimeout
            );
          }

          if (isString(backgroundImage)) {
            const img = new Image();

            img.onload = loadHandler;
            img.onerror = loadHandler;
            img.src = backgroundImage;
          }
        } else if (onLoad) {
          onLoad();
        }
      }
    }, [
      hideUntilBackgroundLoad,
      backgroundImage,
      isMounted,
      backgroundLoadTimeout,
      loadHandler,
      onLoad
    ]);

    if (isHidden) {
      props.style.opacity = 0;
      props.style.pointerEvents = 'none';
    }

    // Background images can be passed as either
    // strings or as data for gatsby-background-image
    if (backgroundImage) {
      const bg = generateBackgroundImage(backgroundImage);

      if (bg) {
        if (bg.type === 'url') {
          props.style.backgroundImage = bg.image;
        } else {
          props[bg.type] = bg.image;
          Tag = BackgroundImage;

          if (hideUntilBackgroundLoad && isHidden) {
            props.onLoad = loadHandler;
            props.onError = loadHandler;
          }
        }
      }
    }

    return (
      <Tag {...props}>
        {!wrapContent ? (
          <React.Fragment>
            {contentBefore}
            {children}
            {contentAfter}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {contentBefore}
            <Content
              className={contentClassName}
              style={{ ...contentWrapperStyle, ...contentStyle }}
              maxWidth={maxContentWidth}
            >
              {children}
            </Content>
            {contentAfter}
          </React.Fragment>
        )}
      </Tag>
    );
  }
);

export default Section;
